/**
 * Normalize an A2UI action envelope into a flat shape we can switch on.
 *
 * The @copilotkit renderer wraps every dispatch as
 *   { userAction: { name, surfaceId, sourceComponentId, context, timestamp } }
 * but flat shapes also appear from admin replay code paths. Centralize
 * the unwrap so both surfaces (iframe widget, admin chat replay) read
 * the same record — a missed unwrap caused silent click failures
 * before and would again the moment a new caller is added.
 */

/**
 * Submit-context keys carrying form field bindings are namespaced by the
 * compiler (backend/chat/a2ui/compiler.py FORM_FIELD_CONTEXT_PREFIX) so a
 * field literally named "name"/"title"/"url" can't collide with action
 * control keys. Values are reconstructed by collecting prefixed keys.
 */
export const FORM_FIELD_CONTEXT_PREFIX = "field:";

export interface UnwrappedAction {
  name: string;
  url: string | undefined;
  text: string | undefined;
  title: string | undefined;
  formId: string | undefined;
  values: Record<string, unknown> | undefined;
  idempotencyKey: string | undefined;
  actionToken: string | undefined;
  raw: Record<string, unknown>;
}

export function unwrapAction(envelope: unknown): UnwrappedAction | null {
  if (!envelope || typeof envelope !== "object") return null;
  const outer = envelope as Record<string, unknown>;
  const inner =
    outer.userAction && typeof outer.userAction === "object"
      ? (outer.userAction as Record<string, unknown>)
      : outer;
  const name =
    typeof inner.action === "string"
      ? inner.action
      : typeof inner.name === "string"
        ? inner.name
        : "";
  if (!name) return null;
  const ctx =
    inner.context && typeof inner.context === "object"
      ? (inner.context as Record<string, unknown>)
      : inner;

  const pickString = (key: string): string | undefined => {
    const fromCtx = ctx[key];
    if (typeof fromCtx === "string") return fromCtx;
    const fromInner = inner[key];
    return typeof fromInner === "string" ? fromInner : undefined;
  };

  // Form field values. Two shapes are accepted:
  //   1. Legacy/explicit: a nested ``values`` object in the context.
  //   2. A2UI-native: a flat ``submit_form`` context where each field's
  //      resolved value lives under a ``field:<name>`` key (the compiler
  //      can't nest an object under context — A2UI only allows
  //      DynamicValues there). The prefix makes reconstruction
  //      unambiguous: any field name is legal, including ones that
  //      collide with control keys like ``name`` or ``title``.
  const values = (() => {
    const v = ctx.values;
    if (v && typeof v === "object") return v as Record<string, unknown>;
    if (name !== "submit_form") return undefined;
    const collected: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(ctx)) {
      if (!key.startsWith(FORM_FIELD_CONTEXT_PREFIX)) continue;
      collected[key.slice(FORM_FIELD_CONTEXT_PREFIX.length)] = val;
    }
    return Object.keys(collected).length > 0 ? collected : undefined;
  })();

  return {
    name,
    url: pickString("url"),
    text: pickString("text") ?? pickString("value"),
    title: pickString("title"),
    formId: pickString("form_id") ?? pickString("formId"),
    values,
    idempotencyKey:
      pickString("idempotency_key") ?? pickString("idempotencyKey"),
    actionToken: pickString("action_token") ?? pickString("actionToken"),
    raw: inner,
  };
}
